const SAME_ORIGIN_CHECK_BASE = 'https://app.todesktop.com';

/**
 * Only same-origin paths are valid destinations. `//host`, `/\host` and
 * absolute URLs leave the app, and history.replaceState throws on them.
 */
const isSameOriginPath = (path: string) => {
  if (!path.startsWith('/')) return false;
  try {
    return (
      new URL(path, SAME_ORIGIN_CHECK_BASE).origin === SAME_ORIGIN_CHECK_BASE
    );
  } catch {
    return false;
  }
};

/** Read both encoded return URLs and legacy links containing a raw URL tail. */
export function getAuthRedirect(search: string, hash = ''): string {
  const parameter = /(?:^\?|&)redirectTo=/.exec(search);
  if (!parameter) return '/';
  const value = search.slice(parameter.index + parameter[0].length);
  // Legacy links include the destination's query parameters and fragment directly.
  if (value.startsWith('/')) {
    return isSameOriginPath(value) ? value + hash : '/';
  }
  try {
    const decoded = decodeURIComponent(value.split('&')[0]);
    return isSameOriginPath(decoded) ? decoded : '/';
  } catch {
    return '/';
  }
}

/**
 * The single sign-on login page, for an email address or domain when
 * there's one, keeping where to go after signing in. `redirectTo` goes last:
 * legacy links put a raw destination after it.
 */
export function ssoLoginPath({
  domain,
  email,
  redirectTo = '/',
}: {
  domain?: string;
  email?: string;
  redirectTo?: string;
} = {}): string {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  else if (domain) params.set('domain', domain);
  if (redirectTo !== '/') params.set('redirectTo', redirectTo);
  const query = params.toString();
  return query ? `/login/sso?${query}` : '/login/sso';
}
